
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useAuthFlow } from "@/hooks/useAuthFlow";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";

const loginSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
});

const signupSchema = loginSchema.extend({
  name: z.string().min(2, "O nome deve ter pelo menos 2 caracteres"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

export function LoginForm() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { signIn, signUp, isLoading } = useAuthFlow();
  const [activeTab, setActiveTab] = useState<string>("login");
  const [debugInfo, setDebugInfo] = useState<string>("");

  // Verificar estado da sessão ao montar o componente
  useState(() => {
    const checkSession = async () => {
      try {
        console.log("LoginForm: Verificando sessão existente...");
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("LoginForm: Erro ao verificar sessão:", error);
        } else if (data.session) {
          console.log("LoginForm: Sessão encontrada, redirecionando...");
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error("LoginForm: Erro ao verificar sessão:", err);
      }
    };

    checkSession();
  }, [navigate]);

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const signupForm = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      name: "",
    },
  });

  const onLogin = async (data: z.infer<typeof loginSchema>) => {
    try {
      console.log("LoginForm: Iniciando login para:", data.email);
      setDebugInfo("");
      
      const userData = await signIn(data.email, data.password);
      
      toast({
        title: "Login realizado com sucesso!",
        description: `Bem-vindo, ${userData.name}!`,
      });
      
      console.log("LoginForm: Login bem-sucedido, redirecionando...");
      navigate("/", { replace: true });
    } catch (error: any) {
      console.error("LoginForm: Erro durante o login:", error);
      
      // Informações de debug para ambiente de desenvolvimento
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('lovable.app')) {
        setDebugInfo(`Erro de autenticação: ${error.message || "Erro desconhecido"}`);
      }
    }
  };

  const onSignup = async (data: z.infer<typeof signupSchema>) => {
    try {
      console.log("LoginForm: Iniciando registro para:", data.email);
      setDebugInfo("");
      
      const userData = await signUp(data.email, data.password, data.name);
      
      toast({
        title: "Registro realizado com sucesso!",
        description: `Bem-vindo, ${userData.name}!`,
      });
      
      console.log("LoginForm: Registro bem-sucedido, redirecionando...");
      navigate("/", { replace: true });
    } catch (error: any) {
      console.error("LoginForm: Erro durante o registro:", error);
      
      // Informações de debug para ambiente de desenvolvimento
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('lovable.app')) {
        setDebugInfo(`Erro de registro: ${error.message || "Erro desconhecido"}`);
      }
    }
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="login">Login</TabsTrigger>
        <TabsTrigger value="signup">Cadastro</TabsTrigger>
      </TabsList>

      <TabsContent value="login">
        <Form {...loginForm}>
          <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
            <FormField
              control={loginForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={loginForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Senha</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                "Entrar"
              )}
            </Button>
            
            {debugInfo && (
              <div className="mt-2 p-2 text-xs bg-red-50 text-red-600 rounded border border-red-200">
                {debugInfo}
              </div>
            )}
          </form>
        </Form>
      </TabsContent>

      <TabsContent value="signup">
        <Form {...signupForm}>
          <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
            <FormField
              control={signupForm.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Seu nome"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={signupForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={signupForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Senha</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={signupForm.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirmar Senha</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registrando...
                </>
              ) : (
                "Cadastrar"
              )}
            </Button>
          </form>
        </Form>
      </TabsContent>
    </Tabs>
  );
}
